import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const slipFallSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/slip-and-fall#webpage`,
      url: `${siteUrl}/slip-and-fall`,
      name: 'Slip and Fall Attorney Fishers Indiana | Premises Liability Lawyer | Jun Law Firm',
      description:
        'Injured in a slip and fall accident in Indiana? Property owners owe you a duty of care. Jun Law Firm fights for immigrant victims to recover full compensation. Free consultation. (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/slip-and-fall#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Slip and Fall', item: `${siteUrl}/slip-and-fall` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/slip-and-fall#service`,
      name: 'Jun Law Firm - Slip and Fall Attorney Indiana',
      url: `${siteUrl}/slip-and-fall`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Premises liability and slip and fall attorney in Indiana helping immigrant victims recover compensation from negligent property owners. Free case review.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What should I do immediately after a slip and fall accident?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Immediately report your fall to the property owner or manager, document the hazard and your injuries with photos, get witness contact information, seek medical attention, preserve the clothing you wore, and contact a slip and fall attorney before signing any documents.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I prove a property owner was negligent in a slip and fall case?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You must show the property owner knew or should have known about the dangerous condition, failed to fix it or warn visitors, and that the hazard directly caused your injury. Evidence such as photos, incident reports, and witness statements are critical.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long do I have to file a slip and fall lawsuit in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'In Indiana, the statute of limitations for personal injury cases including slip and fall is generally 2 years from the date of the accident. Contact an attorney promptly to protect your rights.',
          },
        },
      ],
    },
  ],
};

export default function SlipAndFall() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add scroll animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const commonLocations = [
    {
      number: 'I',
      title: 'Retail Stores',
      description: 'Grocery stores, department stores, and shopping malls with wet floors, cluttered aisles, or inadequate lighting.'
    },
    {
      number: 'II',
      title: 'Restaurants',
      description: 'Spills, grease on floors, uneven surfaces, and poor maintenance in dining areas and restrooms.'
    },
    {
      number: 'III',
      title: 'Apartment Buildings',
      description: 'Broken stairs, inadequate lighting, icy walkways, and poorly maintained common areas.'
    },
    {
      number: 'IV',
      title: 'Medical Facilities',
      description: 'Hospitals, clinics, and nursing homes with wet floors, cluttered hallways, and inadequate safety measures.'
    },
    {
      number: 'V',
      title: 'Hotels & Motels',
      description: 'Slippery lobby floors, poorly maintained pools, broken elevators, and unsafe parking lots.'
    },
    {
      number: 'VI',
      title: 'Public Property',
      description: 'Sidewalks, parks, government buildings with defects, ice, or inadequate maintenance.'
    }
  ];

  const commonCauses = [
    'Wet or slippery floors without warning signs',
    'Uneven or broken pavement and sidewalks',
    'Poor lighting in stairways and walkways',
    'Torn carpeting or loose floor mats',
    'Ice and snow accumulation on walkways',
    'Cluttered aisles and walkways',
    'Broken or missing handrails',
    'Potholes in parking lots',
    'Freshly waxed floors without warnings',
    'Defective stairs or steps'
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Slip and Fall Attorney Fishers Indiana | Premises Liability Lawyer | Jun Law Firm"
        description="Injured in a slip and fall accident in Indiana? Property owners owe you a duty of care. Jun Law Firm fights for immigrant victims to recover full compensation. Free consultation. (317) 701-2768."
        keywords="slip and fall attorney Indiana, premises liability lawyer Fishers, property owner negligence attorney, fall injury lawyer Indiana"
        canonicalPath="/slip-and-fall"
        schema={slipFallSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase font-light" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Premises Liability Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Slip and Fall <em className="font-light italic">Attorney</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Injured in a slip and fall accident in Indiana? Property owners have a legal duty to maintain safe premises. Jun Law Firm helps immigrant victims recover compensation for injuries caused by dangerous conditions on someone else's property.
            </p>
            <div className="flex gap-6">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Free Case Review
              </button>
              <a 
                href="tel:+13177012768"
                className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Call: (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Common Locations */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Common Locations
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Where <em className="italic">Slip and Fall</em> Accidents Happen
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Slip and fall accidents can happen anywhere. We hold negligent property owners accountable for unsafe conditions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {commonLocations.map((location, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {location.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {location.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {location.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Common Causes */}
      <section className="py-24 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Legal Liability
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Common <em className="italic">Causes</em> of Accidents
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mb-12" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Property owners in Indiana have a legal duty to maintain safe premises and warn visitors of known hazards. When they fail to do so, they can be held liable for injuries.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {commonCauses.map((cause, index) => (
              <article key={index} className="border-l-2 border-gray-300 pl-6 hover:border-gray-900 transition-colors">
                <div className="text-xs tracking-widest text-gray-500 mb-2 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <p className="text-base text-gray-700" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {cause}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What to Do */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Important Steps
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              What to Do <em className="italic">After</em> a Slip and Fall
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Taking the right steps immediately after your accident can strengthen your premises liability claim.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            <article className="border-l-2 border-gray-900 pl-8">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                01
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Report the Incident
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Immediately report your fall to the property owner, manager, or staff. Ask them to document the incident in writing and get a copy of the report.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                02
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Document Everything
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Take photos of the hazard that caused your fall, your injuries, and the surrounding area. Get contact information from any witnesses.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                03
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Seek Medical Care
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Get medical attention immediately, even if you think your injuries are minor. Some injuries worsen over time, and medical records are crucial evidence.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                04
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Preserve Evidence
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Keep the shoes and clothing you were wearing. Don't wash them, as they may contain evidence of the hazardous condition.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                05
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Don't Give Statements
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Don't sign any documents or give recorded statements to insurance companies without consulting an attorney first.
              </p>
            </article>

            <article className="border-l-2 border-gray-900 pl-8">
              <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                06
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4" style={{ fontFamily: "'Crimson Pro', serif" }}>
                Contact an Attorney
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Call Jun Law Firm immediately for a free consultation. We'll protect your rights and handle all communications with insurance companies.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact-section" className="py-24 bg-gray-900 text-white border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest text-gray-400 mb-6 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
            Take Action Now
          </div>
          <h2 className="text-5xl md:text-6xl font-light mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Hold Property Owners <em className="italic">Accountable</em>
          </h2>
          <div className="w-16 h-px bg-white mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
            Don't let property owners escape responsibility for your injuries. Contact Jun Law Firm today for a free case evaluation.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="tel:+13177012768"
              className="bg-white text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Call: (317) 701-2768
            </a>
            <button 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const contactEl = document.getElementById('contact');
                    if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }, 300);
              }}
              className="border-2 border-white text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-white hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Online Contact Form
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
